<template>
  <v-container fluid grid-list-xl>
    <v-card>
      <v-toolbar
        color="secondary"
        dark
      >
        <v-toolbar-side-icon><v-icon>group</v-icon></v-toolbar-side-icon>
        <v-toolbar-title>{{$tc('general.user', 2)}}</v-toolbar-title>
        <v-spacer></v-spacer>
        <div
          v-if="$vuetify.breakpoint.smAndDown"
          slot="extension"
          class="white--text w-100"
        >
          <v-text-field
            type="text"
            v-model="filterText"
            @keyup.enter="doFilter"
            :placeholder="$t('admin.usersFilterPlaceholder')"
            single-line
            hide-details
          ></v-text-field>
        </div>
        <v-text-field
          v-if="$vuetify.breakpoint.mdAndUp"
          type="text"
          v-model="filterText"
          @keyup.enter="doFilter"
          :placeholder="$t('admin.usersFilterPlaceholder')"
          single-line
          hide-details
        ></v-text-field>
        <v-btn
          @click.prevent="doFilter"
          icon
        >
          <v-icon>search</v-icon>
        </v-btn>

        <v-btn
          @click.prevent="resetFilter"
          icon
        >
          <v-icon>restore</v-icon>
        </v-btn>
        <transition
          enter-active-class="animated bounceIn"
          leave-active-class="animated bounceOut"
        >
          <v-btn
            v-if="selectedRows !== undefined && selectedRows.length !== 0"
            @click.prevent="massDeleteModalVisible = true"
            icon
          >
            <v-icon>delete</v-icon>
          </v-btn>
        </transition>
      </v-toolbar>
      <v-card-text>
        <v-progress-linear
          v-show="loading"
          slot="progress"
          color="accent"
          indeterminate
        ></v-progress-linear>
        <vuetable
          :api-url="`${this.apiUrl}api/organization-users`"
          :http-fetch="httpFetch"
          :fields="fields"
          :css="css.table"
          :sort-order="sortOrder"
          ref="vuetable"
          :multi-sort="true"
          data-path="data"
          pagination-path="meta"
          track-by="id"
          :append-params="moreParams"
          @vuetable:pagination-data="onPaginationData"
          @vuetable:loading="onLoading"
          @vuetable:loaded="onLoaded"
        >
          <div
            slot="roles"
            slot-scope="props"
          >
            <v-chip
              outline
              v-for="role in props.rowData.roles"
              :key="role">{{ role  }}</v-chip>
          </div>
          <div
            v-if="$store.getters.hasPermission('edit_organization_users')"
            slot="actions-slot"
            slot-scope="props"
          >
            <v-btn
              @click="showEditUserDialog(props.rowData)"
              flat
              icon
            >
              <v-icon>edit</v-icon>
            </v-btn>
            <v-btn
              @click="showDeleteUserDialog(props.rowData)"
              flat
              icon
            >
              <v-icon>delete</v-icon>
            </v-btn>
          </div>
        </vuetable>
      </v-card-text>

      <v-card-actions>
        <vuetable-pagination ref="pagination"
          :css="css.pagination"
          @vuetable-pagination:change-page="onChangePage"
        ></vuetable-pagination>
      </v-card-actions>
    </v-card>

    <v-dialog
      v-if="$store.getters.hasPermission('edit_organization_users')"
      v-model="deleteModalVisible"
      max-width="500px"
    >
      <v-card>
        <v-card-title primary-title>
          <h3 class="headline">{{ $tc("admin.deleteUser", 1) }}</h3>
        </v-card-title>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            @click="deleteModalVisible = false"
            flat
          >{{ $t("general.close") }}</v-btn>
          <v-btn
            @click.prevent="deleteUser()"
            color="accent"
          >{{ $t("general.delete") }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      v-if="$store.getters.hasPermission('edit_organization_users')"
      v-model="editModalVisible"
      max-width="500px"
    >
      <v-card>
        <v-card-title primary-title>
          <h3 class="headline">{{ $t("admin.editUser") }}</h3>
        </v-card-title>
        <v-card-text>
          <v-layout>
            <v-flex>
              <v-text-field
                v-model="currentUser.name"
                name="name"
                prepend-icon="person"
                type="text"
                :label="$t('general.name')"
                single-line
              ></v-text-field>
              <v-text-field
                v-model="currentUser.email"
                name="email"
                prepend-icon="mail"
                type="email"
                :label="$t('auth.email')"
                single-line
              ></v-text-field>
              <v-text-field
                v-model="currentUser.password"
                name="password"
                prepend-icon="lock"
                type="password"
                :label="$t('auth.password')"
                single-line
              ></v-text-field>
              <v-select
                :label="$tc('admin.role', 2)"
                :items="roles"
                :menu-props="{maxHeight: '400'}"
                v-model="currentUser.roles"
                item-text="name"
                item-value="name"
                name="roles"
                multiple
                single-line
                persistent-hint
              ></v-select>
              <v-select
                :label="$tc('admin.permission', 2)"
                :items="permissions"
                :menu-props="{maxHeight: '400'}"
                v-model="currentUser.permissions"
                item-text="name"
                item-value="name"
                name="permissions"
                multiple
                single-line
                persistent-hint
              >
                <template
                  slot="selection"
                  slot-scope="{ item, index }"
                >
                  <v-chip v-if="index === 0">
                    <span>{{ item.name }}</span>
                  </v-chip>
                  <span
                    v-if="index === 1"
                    class="grey--text caption"
                  >(+{{ currentUser.permissions.length - 1 }} others)</span>
                </template>
              </v-select>
            </v-flex>
          </v-layout>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            @click="editModalVisible = false"
            flat
          >{{ $t("general.close") }}</v-btn>
          <v-btn
            @click.prevent="editUser()"
            color="accent"
          >{{ $t("general.edit") }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      v-if="$store.getters.hasPermission('edit_organization_users')"
      v-model="createModalVisible"
      max-width="500px"
    >
      <v-card>
        <v-card-title primary-title>
          <h3 class="headline">{{ $t("admin.createUser") }}</h3>
        </v-card-title>
        <v-card-text>
          <v-layout>
            <v-flex xs12>
              <v-text-field
                v-model="newUser.name"
                v-validate="{ required: true }"
                data-vv-name="name"
                name="name"
                prepend-icon="person"
                type="text"
                :label="$t('general.name')"
                :error="errors.has('name')"
                :error-messages="errors.collect('name')"
                single-line
                required
              ></v-text-field>
              <v-text-field
                v-model="newUser.email"
                v-validate="{ required: true, email: true, min: 5 }"
                data-vv-name="email"
                name="email"
                prepend-icon="mail"
                type="email"
                :label="$t('auth.email')"
                :error="errors.has('email')"
                :error-messages="errors.collect('email')"
                single-line
                required
              ></v-text-field>
              <v-text-field
                v-model="newUser.password"
                v-validate="{ required: true, min: 8 }"
                data-vv-name="password"
                name="password"
                prepend-icon="lock"
                type="password"
                :label="$t('auth.password')"
                :error="errors.has('password')"
                :error-messages="errors.collect('password')"
                single-line
                required
              ></v-text-field>
              <v-text-field
                v-model="newUser.password_confirmation"
                v-validate="{ required: true, is:newUser.password, min: 8 }"
                data-vv-name="password_confirmation"
                name="password_confirmation"
                prepend-icon="lock"
                type="password"
                :label="$t('admin.confirmPassword')"
                :error="errors.has('password_confirmation')"
                :error-messages="errors.collect('password_confirmation')"
                single-line
                required
              ></v-text-field>
              <v-select
                v-model="newUser.roles"
                v-validate="{ required: true }"
                data-vv-name="roles"
                name="roles"
                item-text="name"
                item-value="name"
                :menu-props="{maxHeight: '400'}"
                :label="$tc('admin.role', 2)"
                :items="roles"
                :error="errors.has('roles')"
                :error-messages="errors.collect('roles')"
                multiple
                single-line
                persistent-hint
                required
              ></v-select>
            </v-flex>
          </v-layout>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            @click="createModalVisible = false"
            flat
          >{{ $t("general.close") }}</v-btn>
          <v-btn
            @click.prevent="createUser()"
            color="accent"
          >{{ $t("general.create") }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      v-if="$store.getters.hasPermission('edit_organization_users')"
      v-model="massDeleteModalVisible"
      max-width="500px"
    >
      <v-card>
        <v-card-title primary-title>
          <h3 class="headline">{{ $tc("admin.deleteUser", 2) }}</h3>
        </v-card-title>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            @click="massDeleteModalVisible = false"
            flat
          >{{ $t("general.close") }}</v-btn>
          <v-btn
            @click.prevent="massDeleteUsers()"
            color="accent"
          >{{ $t("general.delete") }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-speed-dial
      v-if="$store.getters.hasPermission('edit_organization_users')"
      fixed
      bottom
      right
      open-on-hover
    >
      <v-btn
        slot="activator"
        color="accent"
        dark
        fab
        hover
        @click="createModalVisible = true"
      >
        <v-icon>add</v-icon>
      </v-btn>
    </v-speed-dial>

  </v-container>
</template>

<script>
import Vue from 'vue';
import VeeValidate from 'vee-validate';
import VueTableMaterialDesignCheckbox from '@/components/generic/VueTableMaterialDesignCheckbox.vue';
import VueTableMixin from '@/components/generic/VueTableMixin';

Vue.use(VeeValidate, { fieldsBagName: 'veeErrors' });

export default {
  mixins: [
    VueTableMixin,
  ],
  data() {
    return {
      fields: [
        {
          name: VueTableMaterialDesignCheckbox,
          titleClass: 'text-xs-left',
          dataClass: 'text-xs-left',
        },
        {
          name: 'id',
          title: 'ID',
          sortField: 'id',
          titleClass: 'text-xs-left',
          dataClass: 'text-xs-left',
        },
        {
          name: 'name',
          title: this.$i18n.t('general.name'),
          sortField: 'name',
          titleClass: 'text-xs-left',
          dataClass: 'text-xs-left',
        },
        {
          name: 'email',
          title: this.$i18n.t('general.emailAddress'),
          sortField: 'email',
          titleClass: 'text-xs-left',
          dataClass: 'text-xs-left',
        },
        {
          name: 'roles',
          title: this.$i18n.tc('admin.role', 1),
          titleClass: 'text-xs-center',
          dataClass: 'text-xs-center',
        },
        {
          name: 'actions-slot',
          title: this.$i18n.tc('general.action', 2),
          titleClass: 'text-xs-center',
          dataClass: 'text-xs-center',
        },
      ],
      css: {
        table: {
          tableClass: 'v-table v-datatable',
          ascendingIcon: 'mdi mdi-sort-ascending',
          descendingIcon: 'mdi mdi-sort-descending',
        },
      },
      sortOrder: [
        { field: 'email', sortField: 'email', direction: 'asc' },
      ],
      currentUser: {
        id: null,
        name: '',
        email: '',
        password: '',
        roles: [],
        permissions: [],
        created_at: {},
        updated_at: {},
      },
      newUser: {
        name: '',
        email: '',
        password: '',
        password_confirmation: '',
        roles: [],
      },
      roles: [],
      permissions: [],
    };
  },
  mounted() {
    this.getItems(
      `${this.apiUrl}api/organization-roles`,
      { params: { paginate: false } },
      'roles',
    );
    this.getItems(
      `${this.apiUrl}api/organization-permissions`,
      { params: { paginate: false } },
      'permissions',
    );
  },
  methods: {
    showEditUserDialog(user) {
      this.currentUser = user;
      this.editModalVisible = true;
    },
    showDeleteUserDialog(user) {
      this.currentUser = user;
      this.deleteModalVisible = true;
    },
    deleteUser() {
      this.deleteModalVisible = false;
      this.deleteItem('user', this.currentUser.id);
    },
    massDeleteUsers() {
      this.massDeleteModalVisible = false;
      this.massDeleteItems('users', {
        data: {
          ids: this.selectedRows,
        },
      });
    },
    editUser() {
      this.editModalVisible = false;
      this.editItem('organization-user', this.currentUser.id, {
        name: this.currentUser.name,
        email: this.currentUser.email,
        password: this.currentUser.password,
        roles: this.currentUser.roles,
        permissions: this.currentUser.permissions,
      });
    },
    createUser() {
      this.createModalVisible = false;
      this.createItem('organization-user', {
        name: this.newUser.name,
        email: this.newUser.email,
        password: this.newUser.password,
        password_confirmation: this.newUser.password_confirmation,
        roles: this.newUser.roles,
      });
      this.resetNewUserData();
    },
    resetNewUserData() {
      this.newUser.name = '';
      this.newUser.email = '';
      this.newUser.password = '';
      this.newUser.password_confirmation = '';
      this.newUser.roles = [];
    },
  },
};
</script>

<style scoped>

</style>
